// ABOUTME: MakeItRight button component that allows parents to cancel out squares for heart calculations
// ABOUTME: Shows handshake icon with states (disabled/enabled/clicked) and handles permissions

import React, { useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PonoIcon } from '../utils/behaviorUtils';
import { BehaviorType } from '../types/api';
import { StyledActionButton, mobileIconOnlySx } from './shared/StyledActionButton';
import { BilingualLabel } from './BilingualLabel';
import { useEffectiveLabelLanguageMode } from '../hooks/useEffectiveLabelLanguageMode';
import naluV1Theme from '../theme/theme.nalu-v1';

// Create a specialized version that extends StyledActionButton for MakeItRight-specific states
const MakeItRightButtonStyled = styled(StyledActionButton)(({ theme, variant }) => {
  const isNaluV1 = theme.logoSrc === naluV1Theme.logoSrc;
  return {
  // Outlined state (enabled but not clicked)
  ...(variant === 'outlined' && {
    backgroundColor: isNaluV1 ? theme.palette.background.default : 'transparent',
    borderColor: isNaluV1 ? theme.palette.secondary.light : theme.palette.secondary.main,
    borderWidth: isNaluV1 ? 1.5 : undefined,
    color: isNaluV1 ? theme.palette.secondary.dark : theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: isNaluV1 ? theme.palette.background.paper : theme.palette.secondary.main + '0A', // 4% opacity
    },
  }),

  // Contained state (balanced/clicked state)
  ...(variant === 'contained' && {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  }),

  // Special disabled state handling for MakeItRight
  '&.Mui-disabled': {
    // Only apply grey styling when there are no squares at all
    '&[data-no-squares="true"]': {
      backgroundColor: theme.palette.grey[300] + ' !important',
      color: theme.palette.grey[500] + ' !important',
      opacity: 0.6,
    },

    // For balanced state (squares = make it rights > 0), keep purple color
    '&[data-no-squares="false"]': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      opacity: 0.9, // Slightly dimmed to show it's not clickable
    },
  },
  };
});

interface MakeItRightButtonProps {
  child: {
    childId: number | string;
    name?: string;
    id?: number | string;
  };
  isParentView: boolean;
  dailySquareCount: number;
  dailyMakeItRightCount: number;
  onBehaviorRecorded?: (behaviorType: BehaviorType) => void;
}

const MakeItRightButton: React.FC<MakeItRightButtonProps> = ({
  child,
  isParentView,
  dailySquareCount,
  dailyMakeItRightCount,
  onBehaviorRecorded
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const labelMode = useEffectiveLabelLanguageMode();

  // Don't render for child view
  if (!isParentView) {
    return null;
  }

  // Button states based on squares vs make-it-rights:
  // - Disabled: no squares exist (squares = 0)
  // - Clickable: more squares than make-it-rights (squares > make-it-rights)
  // - Filled/Clicked: squares balanced by make-it-rights (squares = make-it-rights > 0)

  const hasSquares = dailySquareCount > 0;
  const hasBalancedSquares = dailySquareCount === dailyMakeItRightCount && dailySquareCount > 0;
  const hasUnbalancedSquares = dailySquareCount > dailyMakeItRightCount;

  const isEnabled = hasUnbalancedSquares && !isSubmitting;
  const isBalanced = hasBalancedSquares;

  // Show contained (filled) when balanced, outlined when clickable, disabled when no squares
  const variant = isBalanced ? 'contained' : 'outlined';

  const handleClick = async () => {
    if (!isEnabled || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Call parent callback - this will update the counts and re-render
      if (onBehaviorRecorded) {
        onBehaviorRecorded(BehaviorType.MakeItRight);
      }
    } finally {
      // Reset submitting state after a short delay to allow parent state to update
      setTimeout(() => setIsSubmitting(false), 1000);
    }
  };

  const childName = child.name || 'child';
  const ariaLabel = `Make it right for ${childName}`;

  return (
    <MakeItRightButtonStyled
      variant={variant}
      disabled={!isEnabled || isSubmitting}
      onClick={handleClick}
      aria-label={ariaLabel}
      data-variant={variant}
      data-no-squares={!hasSquares}
      data-testid="make-it-right-button"
      startIcon={<PonoIcon data-testid="MakeItRightIcon" />}
      sx={isMobile ? mobileIconOnlySx(theme.logoSrc === naluV1Theme.logoSrc) : undefined}
    >
      {isMobile ? null : <BilingualLabel term="pono" english={'Make It Right'} mode={labelMode ?? 'english-lead'} />}
    </MakeItRightButtonStyled>
  );
};

export default MakeItRightButton;
